import { Auth } from 'aws-amplify'
import { v4 } from 'uuid'

const authInterceptor = {
  request: {
    onFulfilled: async (config) => {
      const session = await Auth.currentSession()
      const accessToken = session.getIdToken().getJwtToken()
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }

      if (
        ['post', 'put', 'patch', 'delete'].includes(
          config.method?.toLowerCase()
        ) &&
        !config.headers['X-Idempotency-Key']
      ) {
        config.headers['X-Idempotency-Key'] = v4()
      }

      return config
    },
    onRejected: (error) => Promise.reject(error),
  },
  response: {
    onRejected: async (error, axiosInstance) => {
      const originalRequest = error.config

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true

        try {
          const session = await Auth.currentSession()
          const newAccessToken = session.getIdToken().getJwtToken()
          const token = `Bearer ${newAccessToken}`

          axiosInstance.defaults.headers.common['Authorization'] = token
          originalRequest.headers.Authorization = token
          this.accessToken = newAccessToken
          return axiosInstance(originalRequest)
        } catch (err) {
          return Promise.reject(err)
        }
      }

      return Promise.reject(error)
    },
  },
}

// Routing cohort for the Central US profile-search latency experiment
export const AZURE_CANARY_GROUP = 'azurecanary'

export const isAzureCanary = (session) =>
  (session.getIdToken().payload['cognito:groups'] ?? []).includes(
    AZURE_CANARY_GROUP
  )

/**
 * Sends requests from users in the azurecanary Cognito group to the Azure
 * profile-search deployment. Without an Azure URL, or without a session,
 * requests keep the instance's default baseURL.
 */
export const profileSearchRoutingInterceptor =
  (azureBaseURL) => async (config) => {
    if (!azureBaseURL) {
      return config
    }
    try {
      const session = await Auth.currentSession()
      if (isAzureCanary(session)) {
        config.baseURL = azureBaseURL
      }
    } catch (error) {
      // No session: the auth interceptor decides what happens to the request
    }
    return config
  }

const commonInterceptor = {
  request: {},
  response: {
    onFulfilled: (response) => response.data,
  },
}

export const withInterceptors = (axiosInstance) => {
  axiosInstance.interceptors.request.use(
    authInterceptor.request.onFulfilled,
    authInterceptor.request.onRejected
  )
  axiosInstance.interceptors.response.use(
    commonInterceptor.response.onFulfilled,
    (error) => authInterceptor.response.onRejected(error, axiosInstance)
  )

  return axiosInstance
}

/** Only for the profile-search instance; the DX instance shares withInterceptors. */
export const withProfileSearchRouting = (
  axiosInstance,
  azureBaseURL = process.env.VUE_APP_REST_API_AZURE_BASE_URL
) => {
  axiosInstance.interceptors.request.use(
    profileSearchRoutingInterceptor(azureBaseURL)
  )
  return axiosInstance
}
